import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { Modal } from '@/components/Modal';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useAuthUser } from '@/hooks/useAuthUser';
import { ORG_OWNER_ROLE } from '@/hooks/usePermissions';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import type { Member } from '@/types/admin-settings';
import { UserAvatar } from '@/components/NavigationBar/UserAvatar';
import { RoleBadge } from './RoleBadge';

interface EditRolesModalProps {
    member: Member;
    onClose: () => void;
    onSaved: () => void;
}

export function EditRolesModal({
    member,
    onClose,
    onSaved,
}: EditRolesModalProps) {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const { user: currentUser } = useAuthUser();
    const { data: allRoles = [] } = useSWR('admin-member-roles', () =>
        AdminSettingsService.getRoles(),
    );
    const [selectedRoles, setSelectedRoles] = useState<string[]>(member.roles);
    const [isOwner, setIsOwner] = useState<boolean>(member.isOrgOwner);
    const [isUserManager, setIsUserManager] = useState<boolean>(
        member.isOrgUserManager,
    );

    const isSelf = currentUser?.id === member.id;
    const canManageOrgRoles =
        currentUser?.roles.includes(ORG_OWNER_ROLE) === true;

    const addRole = (role: string) => {
        setSelectedRoles((prev) =>
            prev.includes(role) ? prev : [...prev, role],
        );
    };

    const removeRole = (role: string) => {
        setSelectedRoles((prev) =>
            prev.filter((existing) => existing !== role),
        );
    };

    const availableToAdd = allRoles.filter(
        (role) => !selectedRoles.includes(role),
    );

    const rolesChanged =
        selectedRoles.length !== member.roles.length ||
        selectedRoles.some((role) => !member.roles.includes(role)) ||
        member.roles.some((role) => !selectedRoles.includes(role));
    const ownershipChanged = isOwner !== member.isOrgOwner;
    const userManagerChanged = isUserManager !== member.isOrgUserManager;
    const hasChanges = rolesChanged || ownershipChanged || userManagerChanged;
    const hasAnyRoleSelected =
        selectedRoles.length > 0 || isOwner || isUserManager;

    const { isSubmitting, run } = useAsyncAction(
        async () => {
            if (rolesChanged) {
                await AdminSettingsService.updateMemberRoles(
                    member.id,
                    member.grantId,
                    { roles: selectedRoles },
                );
            }
            if (ownershipChanged) {
                if (isOwner) {
                    await AdminSettingsService.grantOrgOwner(member.id);
                } else {
                    await AdminSettingsService.revokeOrgOwner(member.id);
                }
            }
            if (userManagerChanged) {
                if (isUserManager) {
                    await AdminSettingsService.grantOrgUserManager(member.id);
                } else {
                    await AdminSettingsService.revokeOrgUserManager(member.id);
                }
            }
            onSaved();
        },
        {
            successMessage: t('members.rolesUpdated'),
            errorMessage: t('members.rolesUpdateFailed'),
        },
    );

    const handleSubmit = () => {
        if (hasAnyRoleSelected) run();
    };

    return (
        <Modal
            isOpen
            onClose={onClose}
            title={
                <div className="flex flex-col gap-2">
                    <span>
                        {t('members.editRolesTitle', { name: member.name })}
                    </span>
                    <div className="flex items-center gap-2.5 text-sm text-grey-200 font-normal">
                        <UserAvatar name={member.name} size="sm" />
                        <span>{member.name}</span>
                    </div>
                </div>
            }
            onCancel={onClose}
            onSubmit={handleSubmit}
            submitText={t('members.saveRoles')}
            submitDisabled={!hasAnyRoleSelected || isSubmitting || !hasChanges}
            submitInProgress={isSubmitting}
            size="small"
        >
            <div className="flex flex-col gap-4 px-6 py-6">
                <p className="text-sm text-grey-300">{member.email}</p>

                <div className="flex flex-col gap-2">
                    <span className="text-xs text-grey-300 uppercase tracking-wide">
                        {t('members.currentRoles')}
                    </span>
                    {!hasAnyRoleSelected ? (
                        <span className="text-sm text-grey-400">
                            {t('members.noRolesSelected')}
                        </span>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {isOwner && (
                                <RoleBadge
                                    key="owner"
                                    role={t('members.owner')}
                                    variant="positive"
                                    onRemove={
                                        isSelf || !canManageOrgRoles
                                            ? undefined
                                            : () => setIsOwner(false)
                                    }
                                />
                            )}
                            {isUserManager && (
                                <RoleBadge
                                    key="user-manager"
                                    role={t('members.userManager')}
                                    variant="positive"
                                    onRemove={
                                        isSelf || !canManageOrgRoles
                                            ? undefined
                                            : () => setIsUserManager(false)
                                    }
                                />
                            )}
                            {selectedRoles.map((role) => (
                                <RoleBadge
                                    key={role}
                                    role={role}
                                    onRemove={
                                        isSelf
                                            ? undefined
                                            : () => removeRole(role)
                                    }
                                />
                            ))}
                        </div>
                    )}
                    {isOwner && isSelf && (
                        <span className="text-xs text-grey-400">
                            {t('members.cannotRevokeOwnSelf')}
                        </span>
                    )}
                    {isUserManager && isSelf && (
                        <span className="text-xs text-grey-400">
                            {t('members.cannotRevokeOwnUserManager')}
                        </span>
                    )}
                </div>

                {(availableToAdd.length > 0 ||
                    (canManageOrgRoles && (!isOwner || !isUserManager))) && (
                    <div className="flex flex-col gap-2">
                        <span className="text-xs text-grey-300 uppercase tracking-wide">
                            {t('members.addRole')}
                        </span>
                        <div className="flex flex-wrap gap-2">
                            {!isOwner && canManageOrgRoles && (
                                <RoleBadge
                                    key="owner"
                                    role={t('members.owner')}
                                    variant="positive"
                                    onClick={() => setIsOwner(true)}
                                />
                            )}
                            {!isUserManager && canManageOrgRoles && (
                                <RoleBadge
                                    key="user-manager"
                                    role={t('members.userManager')}
                                    variant="positive"
                                    onClick={() => setIsUserManager(true)}
                                />
                            )}
                            {availableToAdd.map((role) => (
                                <RoleBadge
                                    key={role}
                                    role={role}
                                    onClick={() => addRole(role)}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
}
